import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { toast } from "react-toastify";
import AdminWrapper from "../../../components/AdminWrapper";
import { BASE_URL } from "../../../config/config";

const SEVERITY_COLOR = { LOW: "default", MEDIUM: "warning", HIGH: "error" };

// A HIGH-severity or illegal/criminal-activity violation report lets a
// landlord skip the tenant's normal notice period entirely — this queue is
// the check on that: nothing here becomes a real move-out request until an
// admin approves it.
export default function ViolationEscalations() {
  const open = useSelector((state) => state.sidebar.open);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState({});
  const [decidingId, setDecidingId] = useState(null);

  const token = localStorage.getItem("token") || sessionStorage.getItem("ots_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/admin/violation-escalations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.data || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load pending escalations");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, decision) => {
    setDecidingId(`${id}:${decision}`);
    try {
      const { data } = await axios.put(
        `${BASE_URL}/admin/violation-escalations/${id}/decide`,
        { decision, note: notes[id] || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        data?.message ||
          (decision === "APPROVE" ? "Escalation approved" : "Escalation rejected")
      );
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record decision");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <>
      <AdminWrapper />
      <Box className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          Violation Escalations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          These reports skip the tenant's normal notice period — approve only
          when the evidence genuinely supports an urgent move-out.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">No escalations are waiting for review.</Alert>
        ) : (
          <Stack spacing={2}>
            {rows.map((r) => (
              <Paper key={r.id} elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip size="small" label={r.reason} />
                      <Chip size="small" label={r.severity} color={SEVERITY_COLOR[r.severity] || "default"} />
                    </Stack>
                    <Typography fontWeight={700}>
                      {r.flatName} — {r.tenantName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Owner: {r.landlordName}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Reported {r.reportedAt ? moment(r.reportedAt).format("DD MMM YYYY") : "—"}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      Requested move-out: {r.requestedDueDate ? moment(r.requestedDueDate).format("DD MMM YYYY") : "—"}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {r.description}
                </Typography>

                {r.evidence?.length > 0 ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                    {r.evidence.map((file, i) => (
                      <Chip
                        key={file.publicId || i}
                        size="small"
                        variant="outlined"
                        icon={<AttachFileIcon fontSize="small" />}
                        label={`Evidence ${i + 1}`}
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    No evidence attached — this shouldn't happen; treat with caution.
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  size="small"
                  label="Note (optional, shown to the owner)"
                  value={notes[r.id] || ""}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  sx={{ mb: 1.5 }}
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={Boolean(decidingId)}
                    onClick={() => decide(r.id, "APPROVE")}
                  >
                    {decidingId === `${r.id}:APPROVE` ? "Approving…" : "Approve — create move-out request"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={Boolean(decidingId)}
                    onClick={() => decide(r.id, "REJECT")}
                  >
                    {decidingId === `${r.id}:REJECT` ? "Rejecting…" : "Reject"}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
}
